import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { CustomDialog } from 'components/dialog/CustomDialog';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import RedButton from 'Styled/RedButton';
import { prizeFileUrl } from 'API/lotteryApi';
import type { IPrizeUpload } from 'types/IPrize';
import type { PrizeRow } from './DrawingBasePrizeRows';

interface IProps {
  open: boolean;
  row: PrizeRow;
  onClose: () => void;
  onChange: (row: PrizeRow) => void;
  onUpload: (file: File) => void;
  onRemoveUpload: (upload: IPrizeUpload) => void;
  onRemovePendingFile: (index: number) => void;
}

const MAX_UPLOADS = 2;

/** Only images get a preview; a PDF keeps its file name as the link text. */
const isImageUpload = (filename: string) => /\.(jpe?g|png|gif|webp)$/i.test(filename);

const PendingFilePreview: React.FC<{ file: File }> = ({ file }) => {
  const previewUrl = React.useMemo(() => (isImageUpload(file.name) ? URL.createObjectURL(file) : null), [file]);

  React.useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const title = `${file.name} – wird beim Speichern hochgeladen`;

  return previewUrl ? (
    <Box component="img" src={previewUrl} alt={file.name} title={title} sx={{ display: 'block', height: 60, maxWidth: 120, objectFit: 'contain', opacity: 0.6 }} />
  ) : (
    <Typography variant="body2" title={title} sx={{ opacity: 0.6 }}>
      {file.name}
    </Typography>
  );
};

/** Title, description and files of a Sachgewinn; changes go to the row right away and are saved with the page. */
export const DrawingBasePrizeDetailsDialog: React.FC<IProps> = ({ open, row, onClose, onChange, onUpload, onRemoveUpload, onRemovePendingFile }) => {
  const fileInput = React.useRef<HTMLInputElement>(null);
  const fileCount = row.uploads.length + row.pendingFiles.length;

  return (
    <CustomDialog open={open} handleClose={onClose} title="Sachgewinn bearbeiten" maxWidth="sm" actions={<RedButton onClick={onClose}>Übernehmen</RedButton>}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 1, minWidth: { sm: 450 } }}>
        <CustomTextField name={`prize-title-${row.key}`} label="Bezeichnung" value={row.title} onChange={(event) => onChange({ ...row, title: event.target.value })} />

        <CustomTextField
          name={`prize-description-${row.key}`}
          label="Beschreibung"
          value={row.description}
          onChange={(event) => onChange({ ...row, description: event.target.value })}
          multiline
          minRows={3}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {row.uploads.map((upload) => (
            <Box key={upload.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <a href={prizeFileUrl(upload.id)} target="_blank" rel="noreferrer" title={upload.url}>
                {isImageUpload(upload.url) ? (
                  <Box component="img" src={prizeFileUrl(upload.id)} alt={upload.url} sx={{ display: 'block', height: 60, maxWidth: 120, objectFit: 'contain' }} />
                ) : (
                  upload.url
                )}
              </a>
              <IconButton size="small" aria-label="Datei löschen" onClick={() => onRemoveUpload(upload)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {row.pendingFiles.map((file, index) => (
            <Box key={`${index}-${file.name}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PendingFilePreview file={file} />
              <IconButton size="small" aria-label="Datei entfernen" onClick={() => onRemovePendingFile(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          {fileCount >= MAX_UPLOADS ? (
            <Typography variant="body2" sx={{ color: '#969696' }}>
              Sie können maximal {MAX_UPLOADS} Dateien hochladen
            </Typography>
          ) : (
            <Box>
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,application/pdf"
                style={{ display: 'none' }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) onUpload(file);
                  // Allow picking the same file again after a delete.
                  event.target.value = '';
                }}
              />
              <RedButton onClick={() => fileInput.current?.click()}>Datei hochladen (JPG, PDF)</RedButton>
            </Box>
          )}
        </Box>
      </Box>
    </CustomDialog>
  );
};
